import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import portalApi, { apiError } from './portalApi';
import { T, sans } from './portalTheme';
import { usePortalAuth } from '../../context/PortalAuthContext';
import { PortalAppShell, PageContainer, PageHeader, Card, Button, Spinner, Alert, Icon, IC, EmptyState } from './PortalWidgets';
import {
  GRADE_COLOR, PortalRatingData, RATING_API, RatingHero, SEV, ScoreRing, portalRatingQuery, startClaim, usePortalRating,
} from './portalRatingShared';

export type { PortalRatingData } from './portalRatingShared';
export { portalRatingQuery, ScoreRing, GRADE_COLOR } from './portalRatingShared';

const catColor = (s: number) => (s >= 90 ? '#0F8A5F' : s >= 80 ? '#4D7C0F' : s >= 70 ? '#B45309' : s >= 60 ? '#C2410C' : '#B91C1C');

// /questionnaire/portal/rating: the vendor's own company rating (FluxWatch's platform scan, never a customer's data).
export default function PortalRating() {
  const qc = useQueryClient();
  const query = usePortalRating();
  const d = query.data;
  const { account } = usePortalAuth() as { account?: { id?: string; name?: string | null } };
  const [error, setError] = useState<string | null>(null);
  const [claiming, setClaiming] = useState(false);
  const claim = async () => {
    setClaiming(true); setError(null);
    try { await startClaim(); } catch (e) { setError(apiError(e, 'Could not open sign-up. Try again.')); setClaiming(false); }
  };

  const switchDomain = useMutation({
    mutationFn: async (domain: string) => (await portalApi.post(`${RATING_API}/domain`, { domain })).data as PortalRatingData,
    onSuccess: data => { setError(null); qc.setQueryData(portalRatingQuery.queryKey, data); },
    onError: e => setError(apiError(e, 'Could not switch the domain.')),
  });
  const retry = useMutation({
    mutationFn: async () => (await portalApi.post(`${RATING_API}/retry`)).data as PortalRatingData,
    onSuccess: data => { setError(null); qc.setQueryData(portalRatingQuery.queryKey, data); },
    onError: e => setError(apiError(e, 'Could not start the assessment again.')),
  });

  return (
    <PortalAppShell>
      <PageContainer>
        <PageHeader title="Your security rating"
          subtitle="How your company looks from the outside: the view the companies you supply get when they assess you."
          action={d && d.options.length > 1 && d.status !== 'unavailable' ? (
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: T.muted, fontFamily: sans }}>
              Rating for
              <select value={d.domain || ''} disabled={switchDomain.isPending} onChange={e => switchDomain.mutate(e.target.value)}
                style={{ padding: '8px 10px', borderRadius: 8, border: `1px solid ${T.bdr}`, fontFamily: sans, fontSize: 13.5, color: T.ink, background: T.surface }}>
                {d.options.map(o => <option key={o.domain} value={o.domain}>{o.domain}{o.label && o.label !== o.domain ? ` (${o.label})` : ''}</option>)}
              </select>
            </label>
          ) : undefined} />

        {error && <div style={{ marginBottom: 16 }}><Alert tone="error">{error}</Alert></div>}

        {query.isPending ? (
          <Card><div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}><Spinner size={24} /></div></Card>
        ) : query.isError || !d ? (
          <Card><Alert tone="error">{apiError(query.error, 'Could not load your rating.')}</Alert></Card>
        ) : d.status === 'unavailable' ? (
          <Card>
            <EmptyState icon={IC.shield} title="No rating for this account">
              {d.reason === 'free_mail'
                ? 'Security ratings are available for company email addresses. Sign in with your work email to see your company\'s rating.'
                : 'A security rating isn\'t available for your account right now.'}
            </EmptyState>
          </Card>
        ) : d.status === 'failed' ? (
          <Card>
            <Alert tone="warning">We couldn't finish assessing {d.domain}. This is usually temporary.</Alert>
            <div style={{ marginTop: 14 }}><Button onClick={() => retry.mutate()} loading={retry.isPending} icon={IC.activity}>Try again</Button></div>
          </Card>
        ) : (
          <>
            <RatingHero accountId={account?.id} firstName={(account?.name || '').split(' ')[0]} onClaim={claim} claiming={claiming} collapsible={false} />
            {d.status === 'ready' && d.rating && <RatingDetail d={d} onClaim={claim} claiming={claiming} />}
          </>
        )}
      </PageContainer>
    </PortalAppShell>
  );
}

function RatingDetail({ d, onClaim, claiming }: { d: PortalRatingData; onClaim: () => void; claiming: boolean }) {
  const r = d.rating!;
  const cats = r.categories?.length ? r.categories : r.weakest_categories;
  const locked = Math.max(0, (r.findings_total ?? 0) - r.top_findings.length);

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <div className="pr-two" style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.25fr)', gap: 20, alignItems: 'start' }}>
        <Card>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.ink }}>Score by category</div>
            <div style={{ fontSize: 12, color: T.faint }}>out of 100 · weakest first</div>
          </div>
          <div style={{ display: 'grid', gap: 13, marginTop: 16 }}>
            {cats.map((c, i) => (
              <div key={c.name}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13.5, marginBottom: 6 }}>
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8, color: T.ink2, fontWeight: 600, minWidth: 0 }}>
                    <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.name}</span>
                    {i < 3 && c.score < 90 && <span style={{ fontSize: 10.5, fontWeight: 800, color: catColor(c.score), background: `${catColor(c.score)}14`, borderRadius: 99, padding: '1px 7px', whiteSpace: 'nowrap' }}>Needs work</span>}
                  </span>
                  <span style={{ fontWeight: 800, color: catColor(c.score), fontVariantNumeric: 'tabular-nums' }}>{c.score}</span>
                </div>
                <div style={{ height: 8, borderRadius: 99, background: T.hover, overflow: 'hidden' }}>
                  <div style={{ width: `${c.score}%`, height: '100%', borderRadius: 99, background: catColor(c.score), transition: 'width .9s ease' }} />
                </div>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.ink }}>What's hurting your rating</div>
            <div style={{ fontSize: 12, color: T.faint }}>{r.findings_total ?? r.top_findings.length} critical, high and medium findings</div>
          </div>
          {r.top_findings.length === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 14, color: T.green, marginTop: 16 }}>
              <Icon d={IC.checkCircle} size={18} /> No critical, high or medium findings. Customers will like what they see.
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 10, marginTop: 16 }}>
              {r.top_findings.map((f, i) => (
                <div key={i} style={{ border: `1px solid ${T.bdr}`, borderRadius: 12, padding: '13px 15px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 11.5, fontWeight: 800, color: SEV[f.severity]?.color, background: SEV[f.severity]?.bg, borderRadius: 999, padding: '2px 9px' }}>
                      {SEV[f.severity]?.label || f.severity}
                    </span>
                    <span style={{ fontSize: 14, fontWeight: 700, color: T.ink }}>{f.title}</span>
                    {f.category && <span style={{ fontSize: 12, color: T.faint }}>{f.category}</span>}
                  </div>
                  {f.summary && <div style={{ fontSize: 13, color: T.slate, marginTop: 6, lineHeight: 1.55 }}>{f.summary}</div>}
                </div>
              ))}

              {locked > 0 && (
                <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', border: `1px dashed ${T.bdrStrong}` }}>
                  <div aria-hidden="true" style={{ filter: 'blur(5px)', opacity: 0.55, padding: '12px 15px', display: 'grid', gap: 10, userSelect: 'none' }}>
                    {[0, 1].map(k => (
                      <div key={k}>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <span style={{ width: 54, height: 16, borderRadius: 99, background: k ? '#FDE68A' : '#FED7AA' }} />
                          <span style={{ width: 160, height: 16, borderRadius: 6, background: T.bdrStrong }} />
                        </div>
                        <div style={{ height: 10, borderRadius: 6, background: T.bdr, marginTop: 8, width: '92%' }} />
                        <div style={{ height: 10, borderRadius: 6, background: T.bdr, marginTop: 5, width: '70%' }} />
                      </div>
                    ))}
                  </div>
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 12, flexWrap: 'wrap', padding: 12, background: 'rgba(255,255,255,.55)' }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, fontWeight: 800, color: T.ink }}>
                      <Icon d={IC.lock} size={16} /> +{locked} more finding{locked === 1 ? '' : 's'} in your full report
                    </span>
                    <Button size="sm" onClick={onClaim} loading={claiming}>Unlock free</Button>
                  </div>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>

      {/* the free-account offer */}
      <Card padding={0}>
        <div className="pr-cta" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr)) auto', gap: 22, alignItems: 'center', padding: '24px 28px' }}>
          {[
            { icon: IC.file, title: `All ${r.checks} checks`, text: 'Every finding, with what to fix and how.' },
            { icon: IC.activity, title: 'Track your score', text: 'History, and alerts the moment it drops.' },
            { icon: IC.users, title: 'Assess your suppliers', text: 'Rate your own vendors the same way.' },
          ].map(f => (
            <div key={f.title} style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
              <span style={{ width: 38, height: 38, borderRadius: 11, background: T.blueBg, color: T.primary, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon d={f.icon} size={18} />
              </span>
              <span>
                <span style={{ display: 'block', fontSize: 14, fontWeight: 800, color: T.ink }}>{f.title}</span>
                <span style={{ display: 'block', fontSize: 13, color: T.muted, marginTop: 2, lineHeight: 1.45 }}>{f.text}</span>
              </span>
            </div>
          ))}
          <div style={{ textAlign: 'center' }}>
            <Button size="lg" onClick={onClaim} loading={claiming}>Get your full report</Button>
            <div style={{ fontSize: 12, color: T.faint, marginTop: 7 }}>Free · no card needed</div>
          </div>
        </div>
      </Card>

      <div style={{ fontSize: 12.5, color: T.faint, lineHeight: 1.6, padding: '0 4px' }}>
        This rating comes from public, passive checks of {d.domain} (DNS, email, TLS, website and public data sources). FluxWatch doesn't
        log in to or test your systems. Companies that sent you a questionnaire keep their own assessment of you, which can differ if it
        was made on another date.
      </div>

      <style>{`
        @media (max-width: 900px) { .pr-two { grid-template-columns: minmax(0, 1fr) !important; } }
        @media (max-width: 1000px) { .pr-cta { grid-template-columns: minmax(0, 1fr) !important; } }
      `}</style>
    </div>
  );
}

/** Kept for older imports: the compact card now lives in RatingHero's collapsed state. */
export function PortalRatingBanner({ onOpen }: { onOpen: () => void }) {
  const { data: d } = usePortalRating();
  if (!d || d.status !== 'ready' || !d.rating) return null;
  const r = d.rating;
  return (
    <button onClick={onOpen} style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 16, textAlign: 'left', fontFamily: sans, cursor: 'pointer',
      background: T.surface, border: `1px solid ${T.bdr}`, borderRadius: 12, padding: '14px 18px', marginBottom: 24 }}>
      <ScoreRing score={r.score} grade={r.grade} size={58} label="" />
      <span style={{ flex: 1, fontSize: 15, fontWeight: 700, color: T.ink }}>
        Your security rating: <span style={{ color: r.grade ? GRADE_COLOR[r.grade] : T.ink }}>{r.score} · Grade {r.grade}</span>
      </span>
      <span style={{ fontSize: 13.5, fontWeight: 700, color: T.primary }}>View rating →</span>
    </button>
  );
}
